/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Button from '@Button';
import ValidationStatus from '../../Data/ValidationStatus';
import { ComponentTypes } from '../../Data/Components';
import { selectMatchedStrategyCollection } from '../../Helpers/strategyCollection.helpers';
import { selectIsAddressListJourney } from '../../Helpers/clientApiJourneyPosition.helpers';
import {
  goToNextScreen,
  goToPreviousScreen,
} from '../../Lib/redux/slices/clientApiJourney';
import { useWidgetHeightRefresh } from '../../Hooks/useWidgetHeightRefresh/useWidgetHeightRefresh';
import { getComponentValidationStatus } from '../__helpers__/component.helpers';
import {
  getRadioChainDisplayState,
  RadioChainDisplayState,
} from '../../Helpers/progressiveDisplay';
import RadioInput from '../RadioInput/RadioInput';
import useClientApiCopy from './copy/useClientApiCopy';
import makePostSubmissionQuestionsStyles from './makePostSubmissionQuestionsStyles';

/**
 * The form's post-submission questions as one journey screen. Each question is
 * the same radio component the page would have drawn, so its answer is
 * recorded like any other; the screen only decides when the customer moves on.
 * When the questions' page builds a progressive chain, the flow's next
 * unanswered question shows, answered ones collapse to a line with Edit, and
 * later ones stay hidden.
 */
const PostSubmissionQuestions = ({ iFrameId, stepKey }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = makePostSubmissionQuestionsStyles({ theme });
  const copy = useClientApiCopy();

  const collection = useSelector(selectMatchedStrategyCollection);
  const components = useSelector((store) => store.components);
  const formId = useSelector((store) => store.formConfig?.formId);
  const progressiveDisplay = useSelector((store) => store.progressiveDisplay);
  const isAddressJourney = useSelector(selectIsAddressListJourney);

  const claimedComponentIds =
    collection?.implementations?.[stepKey]?.componentIds ?? [];
  // A component removed from the form since the collection was snapshotted, or
  // hidden in Chameleon, is nothing to ask, not a broken screen.
  const questions = claimedComponentIds
    .map((id) => components[`component_${id}`])
    .filter((question) => question && !question.input?.hiddenInChameleon);
  const questionIds = questions.map((question) => question.component.id);

  const isEveryQuestionAnswered = questions.every(
    (component) =>
      getComponentValidationStatus({ component }) === ValidationStatus.VALID
  );

  // The page's chain may hold a radio this screen does not draw (one the
  // collection never claimed). The chain this screen follows is the page's
  // chain narrowed to the questions it shows, in page order, so an undrawn
  // radio can never be the one everyone waits for.
  const getScreenChain = (page) => {
    const pageConfig = progressiveDisplay?.pages?.[page];
    if (pageConfig?.inputType !== ComponentTypes.RADIOS) return null;

    const [trigger, ...componentsChain] = [
      pageConfig.trigger,
      ...pageConfig.componentsChain,
    ].filter((id) => questionIds.includes(id));
    if (trigger === undefined) return null;

    return { ...pageConfig, trigger, componentsChain };
  };

  const getDisplayState = (question) =>
    getRadioChainDisplayState({
      pageConfig: getScreenChain(question.component.page),
      components,
      componentId: question.component.id,
    });

  const shownQuestions = questions.filter(
    (question) => getDisplayState(question) !== RadioChainDisplayState.HIDDEN
  );

  // "Question 2 of 4" belongs to the flow's next question only: a question
  // reopened with Edit already sits among the collapsed answers.
  const isReopened = (question) =>
    (
      progressiveDisplay?.pages?.[question.component.page]?.reopenedFields ?? []
    ).includes(question.component.id);

  // Only a question in a chain is ever CURRENT, so a chain always exists here.
  const chainPosition = (question) => {
    const chain = getScreenChain(question.component.page);
    const order = [chain.trigger, ...chain.componentsChain];
    return { n: order.indexOf(question.component.id) + 1, x: order.length };
  };

  const progressLabel = (question) => {
    const { n, x } = chainPosition(question);
    return copy.questions.progress.replace('{n}', n).replace('{x}', x);
  };

  useWidgetHeightRefresh([
    shownQuestions.map((question) => getDisplayState(question)).join(),
  ]);

  const handleContinue = () => {
    if (!isEveryQuestionAnswered) return;
    dispatch(goToNextScreen());
  };

  const handleBack = () => {
    dispatch(goToPreviousScreen());
  };

  return (
    <Box css={classes.container} data-testid="postSubmissionQuestions">
      <Box css={classes.backButton}>
        <MuiButton
          css={[classes.backLink, isAddressJourney && classes.solarBackLink]}
          onClick={handleBack}
          data-testid="questionsBack"
        >
          {copy.questions.back}
        </MuiButton>
      </Box>
      {shownQuestions.map((question) => (
        <Box
          key={question.component.id}
          css={classes.question}
          data-testid={`postSubmissionQuestion-${question.component.id}`}
        >
          {getDisplayState(question) === RadioChainDisplayState.CURRENT &&
            !isReopened(question) && (
              <Box css={classes.progress} data-testid="questionProgress">
                {progressLabel(question)}
              </Box>
            )}
          <RadioInput
            autoAdvance={false}
            componentId={question.component.id}
            iFrameId={iFrameId}
            subheading={question.component.subheading}
            formId={formId}
            isCollapsed={
              getDisplayState(question) === RadioChainDisplayState.COLLAPSED
            }
            // This screen has no page navigation to validate an Other answer
            // later, so every question commits its text on Enter or blur.
            isInProgressiveChain
            editLabel={copy.questions.edit}
          />
        </Box>
      ))}
      <Box css={classes.continueButton}>
        <Button
          dataTestId="questionsContinue"
          label={copy.questions.continue}
          disabled={!isEveryQuestionAnswered}
          onClick={handleContinue}
        />
      </Box>
    </Box>
  );
};

export default PostSubmissionQuestions;
